import { Box, Stack, Typography } from '@mui/material';
import { AnalyticsEventName, trackAmplitudeEvent } from '@schooly/analytics';
import { GroupType } from '@schooly/api';
import { FC, useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

import { AccessDenied, Loading } from '@schooly/style';
import {
  ModalContentStickyTitle,
  ModalContentStickyWrapper,
} from '../../../../components/uikit-components/Modal/Modal.styled';

import { AttendanceGroupPreviewTable } from '../../../Attendance/AttendanceTable/AttendanceGroupPreviewTable';
import { AttendanceGroupStatsSummary } from '../../../Attendance/AttendanceTable/AttendanceGroupStatsSummary';
import { useGroup } from '../../../../context/groups/useGroup';

import { GroupPreviewModalAttendanceTakeButtons } from './GroupPreviewModalAttendanceTakeButtons';
import { useAuth } from '@schooly/components/authentication';
import { canEditAttendanceEntries } from '../../../Attendance/permissions';

export const GroupPreviewModalAttendance: FC = () => {
  const { group } = useGroup();
  const { permissions } = useAuth();

  const isAttendanceViewer = permissions.includes('attendance_viewer');

  const canTakeAttendance = canEditAttendanceEntries(permissions);

  useEffect(() => {
    trackAmplitudeEvent({
      name: AnalyticsEventName.AttendanceGroupView,
    });
  }, []);

  const renderContent = useCallback(() => {
    if (!group) {
      return <Loading />;
    }

    return <AttendanceGroupPreviewTable group={group} />;
  }, [group]);

  if (!isAttendanceViewer) {
    return <AccessDenied />;
  }

  return (
    <ModalContentStickyWrapper>
      <ModalContentStickyTitle
        sx={(theme) => ({
          [theme.breakpoints.down('md')]: {
            '@media (orientation: portrait)': {
              paddingTop: 3,
            },
          },
        })}
      >
        <Stack
          direction="row"
          mt={1}
          mb={2.5}
          gap={1}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h2">
            <FormattedMessage id="section-Attendance" />
          </Typography>
        </Stack>

        <Stack gap={2} mb={1.25}>
          {group?.validity && (
            <Box>
              <AttendanceGroupStatsSummary groupId={group.id} validity={group.validity} />
            </Box>
          )}
          {!!group && (
            <GroupPreviewModalAttendanceTakeButtons
              groupId={group.id}
              isTutorGroup={group.group_type === GroupType.TutorGroup}
              ageGroupId={group.age_groups[0]?.id ?? ''}
              canTakeAttendance={canTakeAttendance}
            />
          )}
        </Stack>
      </ModalContentStickyTitle>

      {renderContent()}
    </ModalContentStickyWrapper>
  );
};
